import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import colors from '../colors';
import useMap from './useMap';
import useMapStyle from './useMapStyle';
import { getCircleCoords } from './utils';

export const defaultStyle = {
  fillColor: colors.BLACK,
  fillOpacity: 1,
  strokeColor: null,
  strokeOpacity: null,
  strokeWeight: null,
  strokeOffset: null
};

function strokeColor(style) {
  return style.strokeColor ? style.strokeColor : style.fillColor;
}

function strokeOpacity(style) {
  return style.strokeOpacity ? style.strokeOpacity : 1;
}

function strokeWeight(style) {
  return style.strokeWeight ? style.strokeWeight : 0;
}

function strokeOffset(style) {
  return style.strokeOffset ? style.strokeOffset : 0;
}

function PolygonCircle({ id, lat, lng, radius, style: customStyle }) {
  const map = useMap();
  const mapStyle = useMapStyle();
  const isMountedRef = useRef(true);

  const style = {
    ...defaultStyle,
    ...customStyle
  };
  const sourceName = `${id}-source`;

  const layer = {
    id,
    type: 'fill',
    source: sourceName,
    paint: {
      'fill-color': style.fillColor,
      'fill-opacity': style.fillOpacity
    }
  };

  const layerOutline = {
    id: `${id}-outline`,
    type: 'line',
    source: sourceName,
    paint: {
      'line-color': strokeColor(style),
      'line-opacity': strokeOpacity(style),
      'line-width': strokeWeight(style),
      'line-offset': strokeOffset(style)
    }
  };

  const addLayer = () => {
    if (!map.getLayer(layer.id)) {
      map.addSource(sourceName, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      map.addLayer(layer).addLayer(layerOutline);
    }
  };

  const setData = () => {
    if (map.getSource(sourceName)) {
      map.getSource(sourceName).setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [getCircleCoords([lng, lat], radius)]
            },
            properties: {
              lat,
              lng
            }
          }
        ]
      });
    }
  };

  useEffect(() => {
    if (!isMountedRef.current) {
      addLayer();
      setData();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  useEffect(() => {
    if (!isMountedRef.current) {
      setData();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, radius]);

  useEffect(() => {
    if (!isMountedRef.current) {
      map.setPaintProperty(id, 'fill-color', style.fillColor);
      map.setPaintProperty(id, 'fill-opacity', style.fillOpacity);
      map.setPaintProperty(`${id}-outline`, 'line-color', strokeColor(style));
      map.setPaintProperty(
        `${id}-outline`,
        'line-opacity',
        strokeOpacity(style)
      );
      map.setPaintProperty(`${id}-outline`, 'line-width', strokeWeight(style));
      map.setPaintProperty(`${id}-outline`, 'line-offset', strokeOffset(style));
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    style.fillColor,
    style.fillOpacity,
    style.strokeColor,
    style.strokeOpacity,
    style.strokeWeight,
    style.strokeOffset
  ]);

  useEffect(() => {
    addLayer();
    setData();

    isMountedRef.current = false;

    return function cleanup() {
      if (map && map.style && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
        map.removeLayer(layerOutline.id);
        map.removeSource(sourceName);
      }
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

PolygonCircle.propTypes = {
  fillStyle: {},
  outlineStyle: {}
};

PolygonCircle.propTypes = {
  id: PropTypes.string.isRequired,
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  radius: PropTypes.number.isRequired,
  style: PropTypes.shape({
    fillColor: PropTypes.string,
    fillOpacity: PropTypes.number,
    strokeColor: PropTypes.string,
    strokeOpacity: PropTypes.number,
    strokeWeight: PropTypes.number,
    strokeOffset: PropTypes.number
  })
};

export default PolygonCircle;
